import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Paper,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkEmailRead as ReadIcon,
  Circle as UnreadIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const safeArray = (val) => Array.isArray(val) ? val : [];

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const userId = user?.userId || user?.attributes?.sub || user?.username;

  // Fetch user notifications - only after login and with optimized intervals
  const { data: notifications = [], isLoading, error } = useQuery(
    ['userNotifications', userId],
    () => userId ? notificationsAPI.getUserNotifications(userId) : Promise.resolve({ data: [] }),
    {
      select: (response) => response.data || [],
      enabled: !!userId && !!user, // Only fetch if user is properly logged in
      refetchInterval: 300000, // Refetch every 5 minutes instead of 1 minute
      refetchIntervalInBackground: false, // Don't refetch when tab is not active
      staleTime: 240000, // Consider data fresh for 4 minutes
      cacheTime: 600000, // Keep in cache for 10 minutes
      retry: 1, // Only retry once on failure
      retryDelay: 5000, // Wait 5 seconds before retry
    }
  );

  // Ensure notifications is always an array
  const notificationsSafe = safeArray(notifications);

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => notificationsAPI.markAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userNotifications', userId]);
      },
    }
  );

  // Don't render notification bell if user is not logged in
  if (!user || !userId) {
    return null;
  }

  // Priority colors
  const priorityColors = {
    LOW: 'success',
    MEDIUM: 'warning', 
    HIGH: 'error',
    URGENT: 'error'
  };

  // Category emojis
  const categoryEmojis = {
    MAINTENANCE: '🔧',
    UTILITY: '⚡',
    FINANCIAL: '💰',
    SECURITY: '🔒',
    COMMUNITY: '👥',
    POLICY: '📋',
    FACILITY: '🏢',
    EMERGENCY: '🚨',
    GENERAL: '📢'
  };

  const unreadCount = notificationsSafe.filter(n => !n.isRead).length;
  const recentNotifications = notificationsSafe.slice(0, 5); // Show only 5 most recent

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.notificationId);
    }
    handleClose();
  };

  const handleViewAll = () => {
    navigate('/notifications-dashboard');
    handleClose();
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM dd, HH:mm');
    }
  };

  const truncateMessage = (message, maxLength = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!userId) {
    return null; // Don't show if user not logged in
  }

  return (
    <>
      <Tooltip title={`${unreadCount} unread notifications`}>
        <IconButton
          size="large"
          aria-label={`notifications ${unreadCount > 0 ? `${unreadCount} unread` : ''}`}
          color="inherit"
          onClick={handleClick}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            maxHeight: 600,
            mt: 1.5,
            '& .MuiMenuItem-root': {
              px: 0, // Remove padding to use our custom content
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            Notifications
            {unreadCount > 0 && (
              <Chip 
                size="small" 
                label={unreadCount} 
                color="error" 
                sx={{ minWidth: 24, height: 20 }}
              />
            )}
          </Typography>
        </Box>

        {/* Loading state */}
        {isLoading && (
          <MenuItem disabled>
            <Typography color="text.secondary">Loading notifications...</Typography>
          </MenuItem>
        )}

        {/* Error state */}
        {error && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%' }}>
              Failed to load notifications
            </Alert>
          </MenuItem>
        )}

        {/* Empty state */}
        {!isLoading && !error && notificationsSafe.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {/* Notifications list */}
        {!isLoading && !error && recentNotifications.length > 0 && (
          <>
            {recentNotifications.map((notification, index) => (
              <div key={notification.notificationId}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    alignItems: 'flex-start',
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: notification.isRead ? 'action.hover' : 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: notification.isRead ? 'grey.300' : priorityColors[notification.priority] + '.light',
                        fontSize: '0.75rem'
                      }}
                    >
                      {categoryEmojis[notification.category] || '📢'}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            flex: 1,
                            fontSize: '0.875rem'
                          }}
                          noWrap
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <UnreadIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {truncateMessage(notification.message)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatNotificationTime(notification.createdAt)}
                          </Typography>
                          <Chip
                            size="small"
                            label={notification.priority}
                            color={priorityColors[notification.priority]}
                            sx={{ height: 16, fontSize: '0.625rem' }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </MenuItem>
                {index < recentNotifications.length - 1 && <Divider />}
              </div>
            ))}

            {/* Footer */}
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="text"
                onClick={handleViewAll}
                sx={{ justifyContent: 'center' }}
              >
                View All Notifications
                <ExpandMoreIcon sx={{ ml: 1 }} />
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;